<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()

useHead({
  link: [
    { rel: 'canonical', href: `${runtimeConfig.public.serviceDomain}/the-beginning` },
  ],
})

useSeoMeta({
  title: 'Code & Magic - プログラミング、始めてみたかった。',
  description: 'あの頃の憧れに、もう一度。レトロゲーム作りで始めるプログラミング体験。',
  ogTitle: 'Code & Magic - プログラミング、始めてみたかった。',
  ogDescription: 'あの頃の憧れに、もう一度。レトロゲーム作りで始めるプログラミング体験。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'simple-landing',
})

// JSON-LD: WebPage + FAQPage + Organization
useJsonLd({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      'name': 'Code & Magic - プログラミング、始めてみたかった。',
      'description': 'あの頃の憧れに、もう一度。レトロゲーム作りで始めるプログラミング体験。',
      'url': `${runtimeConfig.public.serviceDomain}/the-beginning`,
      'publisher': {
        '@type': 'Organization',
        'name': 'サムライアプス株式会社',
        'url': 'https://www.samurai-apps.com/',
        'email': 'support@samurai-apps.com',
        'address': {
          '@type': 'PostalAddress',
          'addressCountry': 'JP',
          'postalCode': '220-0004',
          'addressRegion': '神奈川県',
          'addressLocality': '横浜市西区',
          'streetAddress': '北幸2丁目10番48号むつみビル3階',
        },
      },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'プログラミング経験がなくても大丈夫？',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': '大丈夫です。Code & Magic は「写経」スタイルで、お手本のコードを見ながら書き写すところから始めます。分からないところはAIアドバイザーがヒントをくれます。',
          },
        },
        {
          '@type': 'Question',
          'name': '何が必要ですか？',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'パソコン（Windows/Mac）とインターネット環境、ウェブブラウザがあればOK。特別なソフトのインストールは不要です。',
          },
        },
        {
          '@type': 'Question',
          'name': '無料でどこまでできますか？',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': '無料でも作品を遊んだり、自分でプログラムを作ったりできます。有料エリアでは、より多くのゲームテーマやAI診断機能などが利用できます。',
          },
        },
        {
          '@type': 'Question',
          'name': 'サポートはありますか？',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'メールサポートをご用意しています。また、ゲーム内のAIアドバイザー「女神の審判」がコードの診断とヒントを提供します。',
          },
        },
      ],
    },
  ],
})

const { trackCta } = useCtaEvent()

// 顔はめパネル: ユーザーの入力値
const blankValues = ref<Record<string, string>>({
  blank1: '',
  blank2: '',
  blank3: '',
})

// Pyxel 実行状態
const showPyxel = ref(false)
const storyRevealed = ref(false)

// シーン間の自動スクロール
const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

// 固定の Pyxel コード（ユーザー入力とは無関係に動く）
const pyxelCode = [
  'import pyxel',
  '',
  'class App:',
  '    def __init__(self):',
  '        pyxel.init(64, 64, title=\'Magic\')',
  '        self.t = 0',
  '        pyxel.run(self.update, self.draw)',
  '',
  '    def update(self):',
  '        self.t += 1',
  '',
  '    def draw(self):',
  '        pyxel.cls(0)',
  '        for i in range(20):',
  '            x = 32 + pyxel.sin(self.t * 3 + i * 18) * (10 + i)',
  '            y = 32 + pyxel.cos(self.t * 2 + i * 18) * (10 + i)',
  '            c = (i + self.t // 4) % 15 + 1',
  '            pyxel.circ(x, y, 2, c)',
  '        pyxel.text(20, 29, \'MAGIC!\', pyxel.frame_count % 16)',
  '',
  'App()',
].join('\n')

const onRunClick = () => {
  showPyxel.value = true
  scrollTo('scene-magic')
  // Pyxel の読み込みを待ってからストーリー続きを表示可能にする
  setTimeout(() => {
    storyRevealed.value = true
  }, 3000)
}

interface FAQ {
  id: string
  question: string
  answer: string
}

const faqs = ref<FAQ[]>([
  {
    id: 'faq-001',
    question: 'プログラミング経験がなくても大丈夫？',
    answer: '大丈夫です。Code & Magic は「写経」スタイルで、お手本のコードを見ながら書き写すところから始めます。分からないところはAIアドバイザーがヒントをくれます。',
  },
  {
    id: 'faq-002',
    question: '何が必要ですか？',
    answer: 'パソコン（Windows/Mac）とインターネット環境、ウェブブラウザがあればOK。特別なソフトのインストールは不要です。',
  },
  {
    id: 'faq-003',
    question: '無料でどこまでできますか？',
    answer: '無料でも作品を遊んだり、自分でプログラムを作ったりできます。有料エリアでは、より多くのゲームテーマやAI診断機能などが利用できます。',
  },
  {
    id: 'faq-004',
    question: 'サポートはありますか？',
    answer: 'メールサポートをご用意しています。また、ゲーム内のAIアドバイザー「女神の審判」がコードの診断とヒントを提供します。',
  },
])
</script>

<template>
  <div class="starter-lp">
    <!-- ===== カット1: ヒーロー ===== -->
    <section
      id="scene-hero"
      class="scene hero-scene"
    >
      <img
        src="@/assets/images/beginning/TheBeginning_Cut1.webp"
        alt=""
        class="scene-bg"
      >
      <div class="scene-overlay" />

      <!-- ロゴナビ（ヒーロー画像の左上にオーバーレイ） -->
      <nav class="top-nav">
        <NuxtLink
          to="/"
          class="top-nav-logo"
        >
          <img
            src="@/assets/images/code_and_magic_black_textlogo.svg"
            alt="Code & Magic"
            class="top-nav-logo-img"
          >
        </NuxtLink>
      </nav>

      <div class="scene-content hero-content">
        <h1 class="hero-headline">
          プログラミング、<br>始めてみたかった。
        </h1>
        <p class="hero-sub">
          でも「今さら」って、思ってた。
        </p>
        <p class="hero-message">
          あの頃のワクワクを、もう一度。<br>
          大人だって、遊びながら始めていい。
        </p>
        <button
          class="next-button"
          @click="scrollTo('scene-dream')"
        >
          <span class="next-arrow">&#9661;</span>
        </button>
      </div>
    </section>

    <!-- ===== カット2: 夢の導入 ===== -->
    <section
      id="scene-dream"
      class="scene story-scene"
    >
      <img
        src="@/assets/images/beginning/TheBeginning_Cut2.webp"
        alt=""
        class="scene-bg"
      >
      <div class="scene-overlay dark" />
      <div class="scene-content story-text-block">
        <p class="story-text">
          子供のころの夢をみた。
        </p>
        <p class="story-text">
          どこからともなく聞こえてくる声。
        </p>
        <p class="story-text">
          導かれるように、森の奥へと進んでいく。
        </p>
        <button
          class="next-button"
          @click="scrollTo('scene-codex')"
        >
          <span class="next-arrow">&#9661;</span>
        </button>
      </div>
    </section>

    <!-- ===== カット3: 古ぼけた紙 + 顔はめパネル ===== -->
    <section
      id="scene-codex"
      class="scene codex-scene"
    >
      <img
        src="@/assets/images/beginning/TheBeginning_Cut3.webp"
        alt=""
        class="scene-bg"
      >
      <div class="scene-overlay dark" />
      <div class="scene-content">
        <p class="story-text">
          少しだけ字が消えたような、<br>古ぼけた紙が落ちている。
        </p>
        <p class="story-text small">
          ── 読める部分を、なぞってみよう。
        </p>

        <!-- 顔はめパネル写経体験 -->
        <div class="codex-panel">
          <pre class="codex-code"><span class="code-keyword">import</span> pyxel

pyxel.init(<input
  v-model="blankValues.blank1"
  class="code-blank"
  type="text"
  placeholder="64"
  maxlength="3"
>, <input
  v-model="blankValues.blank2"
  class="code-blank"
  type="text"
  placeholder="64"
  maxlength="3"
>)

<span class="code-keyword">for</span> i <span class="code-keyword">in</span> range(<input
  v-model="blankValues.blank3"
  class="code-blank"
  type="text"
  placeholder="20"
  maxlength="3"
>):
    x = pyxel.sin(i * 18) * 20
    y = pyxel.cos(i * 18) * 20
    pyxel.circ(32+x, 32+y, 2, 7)

pyxel.show()</pre>
          <button
            class="run-button"
            @click="onRunClick"
          >
            <span class="run-icon">&#9654;</span> run
          </button>
        </div>
      </div>
    </section>

    <!-- ===== カット4: 魔法が発動 ===== -->
    <section
      v-if="showPyxel"
      id="scene-magic"
      class="scene magic-scene"
    >
      <img
        src="@/assets/images/beginning/TheBeginning_Cut4.webp"
        alt=""
        class="scene-bg"
      >
      <div class="scene-overlay dark" />
      <div class="scene-content">
        <p class="story-text glow">
          ゲームが動き出した。
        </p>
        <p class="story-text">
          ── これは、魔法なのか？
        </p>
        <div class="pyxel-container">
          <ClientOnly>
            <PyxelPlayer :code="pyxelCode" />
          </ClientOnly>
        </div>
        <button
          class="next-button"
          :style="{ visibility: storyRevealed ? 'visible' : 'hidden' }"
          @click="scrollTo('scene-finale')"
        >
          <span class="next-arrow">&#9661;</span>
        </button>
      </div>
    </section>

    <!-- ===== カット5: 冒険への出発 ===== -->
    <section
      id="scene-finale"
      class="scene finale-scene"
    >
      <img
        src="@/assets/images/beginning/TheBeginning_Cut5.webp"
        alt=""
        class="scene-bg"
      >
      <div class="scene-overlay" />
      <div class="scene-content finale-content">
        <p class="story-text finale-text">
          そして、僕は<br>冒険者を目指すことにしたんだった。
        </p>

        <div class="cta-box">
          <h2 class="cta-title">
            もっと冒険したい？
          </h2>
          <p class="cta-description">
            サインアップすれば、もっとたくさんの作品を遊んだり、<br>
            自分だけのゲームを作ることもできます。<br>
            もちろん無料で始められます。
          </p>
          <button
            class="cta-button primary"
            @click="trackCta('冒険を始める'); navigateTo('/signup')"
          >
            冒険を始める
          </button>
        </div>

        <button
          class="next-button"
          @click="scrollTo('section-faq')"
        >
          <span class="next-arrow">&#9661;</span>
        </button>
      </div>
    </section>

    <!-- ===== FAQ ===== -->
    <section
      id="section-faq"
      class="faq-section"
    >
      <div class="faq-container">
        <h2 class="faq-title">
          よくある質問
        </h2>
        <div class="faq-list">
          <div
            v-for="faq in faqs"
            :key="faq.id"
            class="faq-item"
          >
            <h3 class="faq-question">
              Q. {{ faq.question }}
            </h3>
            <p class="faq-answer">
              A. {{ faq.answer }}
            </p>
          </div>
        </div>

        <div class="faq-cta">
          <button
            class="cta-button primary"
            @click="trackCta('冒険を始める（FAQ）'); navigateTo('/signup')"
          >
            冒険を始める
          </button>
        </div>
      </div>
    </section>

    <!-- ===== 運営者情報 ===== -->
    <section class="operator-section">
      <div class="operator-container">
        <h3 class="operator-title">
          運営
        </h3>
        <p class="operator-name">
          サムライアプス株式会社
        </p>
        <p class="operator-contact">
          <a
            href="https://www.samurai-apps.com/contact/"
            target="_blank"
            rel="noopener"
          >お問い合わせ</a>
        </p>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* ===== 全体 ===== */
.starter-lp {
  font-family: 'Noto-Serif-JP', serif;
  line-height: 1.8;
  color: #e8e0d0;
}

/* ===== ロゴナビ ===== */
.top-nav {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 12px 16px;
}

.top-nav-logo {
  display: inline-block;
  text-decoration: none;
}

.top-nav-logo-img {
  height: 36px;
  width: auto;
  filter: invert(1) drop-shadow(0 0 4px #000);
}

/* ===== シーン共通 ===== */
.scene {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.scene-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.scene-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%);
  z-index: 1;
}

.scene-overlay.dark {
  background: linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%);
}

.scene-content {
  position: relative;
  z-index: 2;
  max-width: 720px;
  padding: 40px 24px;
  text-align: center;
}

/* ===== カット1: ヒーロー ===== */
.hero-content {
  padding-top: 80px;
}

.hero-headline {
  font-size: clamp(1.8rem, 5dvw, 3rem);
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 20px;
  text-shadow: 0 2px 20px rgba(0,0,0,0.7);
}

.hero-sub {
  font-size: clamp(1rem, 3dvw, 1.5rem);
  font-weight: 600;
  margin-bottom: 40px;
  text-shadow: 0 2px 16px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.5);
}

.hero-message {
  font-size: clamp(0.9rem, 2.5dvw, 1.2rem);
  font-weight: 500;
  margin-bottom: 60px;
  text-shadow: 0 2px 16px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.5);
}

/* ===== シーン送りボタン ===== */
.next-button {
  display: block;
  margin: 40px auto 0;
  background: none;
  border: 2px solid rgba(232, 224, 208, 0.5);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.next-button:hover {
  border-color: rgba(232, 224, 208, 0.8);
  background: rgba(232, 224, 208, 0.1);
}

.next-arrow {
  display: block;
  font-size: 1.2rem;
  color: rgba(232, 224, 208, 0.8);
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}

/* ===== ストーリーテキスト共通 ===== */
.story-text-block {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.story-text {
  font-size: clamp(1.1rem, 3dvw, 1.6rem);
  font-weight: 600;
  text-shadow: 0 2px 16px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.5);
}

.story-text.small {
  font-size: clamp(0.9rem, 2dvw, 1.1rem);
  opacity: 0.8;
}

.story-text.glow {
  color: #ffd780;
  text-shadow: 0 0 30px rgba(255,200,80,0.6), 0 2px 16px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.5);
}

.story-text.finale-text {
  font-size: clamp(1.3rem, 4dvw, 2rem);
  margin-bottom: 60px;
}

/* ===== カット3: 顔はめパネル ===== */
.codex-panel {
  margin-top: 40px;
  background: rgba(20, 15, 10, 0.85);
  border: 1px solid rgba(180, 160, 120, 0.3);
  border-radius: 8px;
  padding: 24px;
  text-align: left;
  box-shadow: 0 0 40px rgba(180, 160, 100, 0.1);
}

.codex-code {
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: clamp(0.75rem, 2dvw, 0.95rem);
  line-height: 1.8;
  color: #c8b888;
  white-space: pre-wrap;
  margin: 0;
}

.code-keyword {
  color: #88aacc;
}

.code-blank {
  background: rgba(255, 200, 80, 0.1);
  border: 1px dashed rgba(255, 200, 80, 0.5);
  border-radius: 4px;
  color: #ffd780;
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: inherit;
  width: 3.5em;
  padding: 2px 6px;
  text-align: center;
  outline: none;
}

.code-blank:focus {
  border-color: #ffd780;
  background: rgba(255, 200, 80, 0.2);
  box-shadow: 0 0 10px rgba(255, 200, 80, 0.2);
}

.code-blank::placeholder {
  color: rgba(255, 200, 80, 0.3);
}

.run-button {
  display: block;
  margin: 20px auto 0;
  padding: 12px 48px;
  background: rgba(255, 200, 80, 0.15);
  border: 1px solid rgba(255, 200, 80, 0.5);
  border-radius: 6px;
  color: #ffd780;
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.run-button:hover {
  background: rgba(255, 200, 80, 0.25);
  box-shadow: 0 0 20px rgba(255, 200, 80, 0.2);
  transform: translateY(-1px);
}

.run-icon {
  font-size: 0.9rem;
}

/* ===== カット4: Pyxel 表示 ===== */
.magic-scene {
  animation: fadeIn 1s ease-in;
}

.pyxel-container {
  margin-top: 30px;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1;
  margin-left: auto;
  margin-right: auto;
}

.pyxel-container :deep(.pixel-player) {
  height: 100%;
}

.pyxel-container :deep(.pixel-player-inner) {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  border: 2px solid rgba(180, 160, 120, 0.3);
  border-radius: 8px;
}

/* ===== カット5: CTA ===== */
.finale-scene {
  animation: fadeIn 1s ease-in;
}

.finale-content {
  padding-top: 60px;
  padding-bottom: 60px;
}

.cta-box {
  background: rgba(10, 8, 5, 0.8);
  border: 1px solid rgba(180, 160, 120, 0.3);
  border-radius: 16px;
  padding: 48px 32px;
  backdrop-filter: blur(8px);
}

.cta-title {
  font-size: clamp(1.3rem, 3.5dvw, 1.8rem);
  color: #ffd780;
  margin-bottom: 20px;
}

.cta-description {
  font-size: clamp(0.85rem, 2dvw, 1rem);
  opacity: 0.8;
  margin-bottom: 36px;
  font-weight: 400;
}

.cta-button {
  padding: 18px 60px;
  font-size: 1.2rem;
  font-weight: 700;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
}

.cta-button.primary {
  background: linear-gradient(135deg, #c8a050 0%, #e0c070 100%);
  color: #1a1510;
}

.cta-button.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(200, 160, 80, 0.3);
}

/* ===== FAQ ===== */
.faq-section {
  background: linear-gradient(180deg, #1a1510 0%, #0a0805 100%);
  padding: 80px 24px;
  animation: fadeIn 1s ease-in;
}

.faq-container {
  max-width: 720px;
  margin: 0 auto;
}

.faq-title {
  font-size: 1.6rem;
  text-align: center;
  margin-bottom: 40px;
  color: #c8b888;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.faq-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(180, 160, 120, 0.15);
  padding: 24px;
  border-radius: 10px;
}

.faq-question {
  font-size: 1.05rem;
  margin-bottom: 12px;
  color: #d4c090;
}

.faq-answer {
  font-size: 0.95rem;
  color: #a09880;
  font-weight: 400;
  line-height: 1.8;
}

.faq-cta {
  text-align: center;
  margin-top: 48px;
}

/* ===== 運営者情報 ===== */
.operator-section {
  background: #0a0805;
  padding: 32px 24px;
  text-align: center;
}

.operator-container {
  max-width: 720px;
  margin: 0 auto;
}

.operator-title {
  font-size: 0.85rem;
  color: #a09880;
  margin-bottom: 8px;
}

.operator-name {
  font-size: 0.9rem;
  color: #c8b888;
  margin-bottom: 4px;
}

.operator-contact a {
  font-size: 0.8rem;
  color: #a09880;
  text-decoration: none;
}

.operator-contact a:hover {
  color: #c8b888;
}

/* ===== アニメーション ===== */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ===== レスポンシブ ===== */
@media (max-width: 600px) {
  .scene-content {
    padding: 24px 16px;
  }

  .codex-panel {
    padding: 16px;
  }

  .cta-box {
    padding: 32px 20px;
  }

  .cta-button {
    padding: 16px 40px;
    font-size: 1.1rem;
  }
}
</style>
